package com.arenaproplay.admin.ui.components.overlay

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.ui.components.icons.LucideIcon
import com.arenaproplay.admin.ui.layouts.shell.commandPaletteGroups
import com.arenaproplay.admin.ui.layouts.shell.NavItem
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.sBorderRadius
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent

/**
 * Flat list of all navigable pages shown in the Command Palette.
 * Includes both the new IA surfaces AND the legacy pages (for the R6-R8
 * migration period so power users can still reach old pages via ⌘K).
 */
private val allNavItems: List<NavItem> by lazy {
    commandPaletteGroups.flatMap { it.items }
}

/**
 * ⌘K / Ctrl+K Command Palette overlay.
 *
 * Shows a fuzzy-search dialog over all nav pages.  Keyboard navigation:
 *   ↑/↓  move selection
 *   Enter navigate to selected item
 *   Esc   close
 *
 * The "Users: <query>" shortcut row appears at the bottom when [query] is
 * non-empty and navigates to `/users?q=<query>`.
 */
@Composable
fun CommandPalette(
    isOpen: Boolean,
    onClose: () -> Unit
) {
    if (!isOpen) return

    val ctx = rememberPageContext()
    var query by remember { mutableStateOf("") }
    var selectedIndex by remember { mutableStateOf(0) }
    val inputId = remember { "cmd-palette-input" }

    // Filtered nav items
    val filtered = remember(query) {
        val q = query.trim().lowercase()
        if (q.isEmpty()) allNavItems
        else allNavItems.filter { it.label.lowercase().contains(q) || it.group.lowercase().contains(q) }
    }

    // Whether to show the "Users: <query>" and "Clubs: <query>" action rows
    val showUsersAction = query.trim().isNotEmpty()
    val showClubsAction = query.trim().isNotEmpty()
    val totalItems = filtered.size + (if (showUsersAction) 1 else 0) + (if (showClubsAction) 1 else 0)

    // Clamp selectedIndex when filtered list changes
    LaunchedEffect(totalItems) {
        if (selectedIndex >= totalItems) selectedIndex = 0
    }

    // Auto-focus the input when palette opens
    LaunchedEffect(isOpen) {
        if (isOpen) {
            // Give Compose a frame to render the input before focusing
            kotlinx.coroutines.delay(50)
            (document.getElementById(inputId) as? HTMLInputElement)?.focus()
        }
    }

    // Keyboard handler
    DisposableEffect(isOpen) {
        val handler: (dynamic) -> Unit = { e ->
            val ke = e as KeyboardEvent
            when (ke.key) {
                "ArrowDown" -> {
                    ke.preventDefault()
                    selectedIndex = (selectedIndex + 1) % totalItems.coerceAtLeast(1)
                }
                "ArrowUp" -> {
                    ke.preventDefault()
                    selectedIndex = ((selectedIndex - 1) + totalItems.coerceAtLeast(1)) % totalItems.coerceAtLeast(1)
                }
                "Enter" -> {
                    ke.preventDefault()
                    val q = query.trim()
                    if (showUsersAction && selectedIndex == filtered.size) {
                        // "Users: <query>" action row
                        onClose()
                        ctx.router.navigateTo("${Routes.USERS}?q=$q")
                    } else if (showClubsAction && selectedIndex == filtered.size + 1) {
                        // "Clubs: <query>" action row
                        onClose()
                        ctx.router.navigateTo("${Routes.CLUBS}?q=$q")
                    } else {
                        filtered.getOrNull(selectedIndex)?.let { item ->
                            onClose()
                            ctx.router.navigateTo(item.legacySidebarTarget ?: item.route)
                        }
                    }
                }
                "Escape" -> {
                    ke.preventDefault()
                    onClose()
                }
            }
        }
        document.addEventListener("keydown", handler)
        onDispose { document.removeEventListener("keydown", handler) }
    }

    // ── Overlay scrim ────────────────────────────────────────────────────────
    Box(
        Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .styleModifier {
                property("position", "fixed")
                property("inset", "0")
                property("z-index", "999")
                property("background", "rgba(0,0,0,0.55)")
                property("backdrop-filter", "blur(3px)")
                property("-webkit-backdrop-filter", "blur(3px)")
            }
            .onClick { onClose() },
        contentAlignment = Alignment.TopCenter
    ) {
        // ── Palette card ──────────────────────────────────────────────────────
        Box(
            Modifier
                .width(540.px)
                .margin(top = 100.px)
                .sBorderRadius(14)
                .classNames("na-cmd-palette")
                .styleModifier {
                    property("background", "#0B1220")
                    property("border", "1px solid #1C2634")
                    property("box-shadow", "0 24px 60px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.08)")
                    property("overflow", "hidden")
                }
                .onClick { it.stopPropagation() }
        ) {
            Column(Modifier.fillMaxWidth()) {

                // ── Search input row ─────────────────────────────────────────
                Row(
                    Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 16.px, topBottom = 12.px)
                        .styleModifier {
                            property("border-bottom", "1px solid #141C29")
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    LucideIcon(name = "search", size = 16, color = "#868A91")
                    Box(Modifier.width(10.px))
                    Input(
                        type = InputType.Text,
                        attrs = Modifier
                            .weight(1f)
                            .styleModifier {
                                property("font-size", "15px")
                                property("font-weight", "450")
                                property("color", "#F0F4FC")
                                property("border", "none")
                                property("outline", "none")
                                property("background", "transparent")
                                property("font-family", "'Inter', sans-serif")
                            }
                            .toAttrs {
                                id(inputId)
                                placeholder("Search pages…")
                                value(query)
                                onInput { event ->
                                    query = (event.target as? HTMLInputElement)?.value ?: ""
                                    selectedIndex = 0
                                }
                            }
                    )
                    if (query.isNotEmpty()) {
                        Box(
                            Modifier.cursor(Cursor.Pointer).onClick { query = ""; selectedIndex = 0 }
                        ) {
                            LucideIcon(name = "x", size = 14, color = "#868A91")
                        }
                    }
                    Box(Modifier.width(10.px))
                    Box(
                        Modifier
                            .sBorderRadius(5)
                            .padding(leftRight = 6.px, topBottom = 3.px)
                            .styleModifier {
                                property("background", "#101828")
                                property("border", "1px solid #1C2634")
                            }
                    ) {
                        SpanText(
                            "Esc",
                            Modifier.styleModifier {
                                property("font-family", "'JetBrains Mono', monospace")
                                property("font-size", "10px")
                                property("color", "#868A91")
                            }
                        )
                    }
                }

                // ── Results list ──────────────────────────────────────────────
                Column(
                    Modifier
                        .fillMaxWidth()
                        .styleModifier { property("max-height", "360px") }
                        .overflow(Overflow.Auto)
                        .classNames("no-scrollbar")
                        .padding(topBottom = 6.px, leftRight = 6.px)
                ) {
                    if (filtered.isEmpty() && !showUsersAction) {
                        Box(
                            Modifier.fillMaxWidth().padding(topBottom = 24.px),
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                "No pages found for \"$query\"",
                                Modifier.bodySMedium()
                                    .styleModifier { property("color", "#868A91") }
                            )
                        }
                    } else {
                        // Group items by group label when no query
                        if (query.trim().isEmpty()) {
                            commandPaletteGroups.forEach { group ->
                                val groupItems = filtered.filter { it.group == group.title }
                                if (groupItems.isEmpty()) return@forEach

                                Box(
                                    Modifier
                                        .fillMaxWidth()
                                        .padding(left = 10.px, top = 10.px, bottom = 4.px)
                                ) {
                                    SpanText(
                                        group.title,
                                        Modifier.eyebrow().color(AppColors.Faint)
                                    )
                                }

                                groupItems.forEach { item ->
                                    val idx = filtered.indexOf(item)
                                    PaletteRow(
                                        item = item,
                                        isSelected = idx == selectedIndex,
                                        onSelect = {
                                            onClose()
                                            ctx.router.navigateTo(item.legacySidebarTarget ?: item.route)
                                        }
                                    )
                                }
                            }
                        } else {
                            // Flat list for filtered results
                            filtered.forEachIndexed { idx, item ->
                                PaletteRow(
                                    item = item,
                                    isSelected = idx == selectedIndex,
                                    onSelect = {
                                        onClose()
                                        ctx.router.navigateTo(item.legacySidebarTarget ?: item.route)
                                    }
                                )
                            }
                        }

                        // ── "Users: <query>" action row ──────────────────────
                        if (showUsersAction) {
                            val usersIdx = filtered.size
                            val q = query.trim()
                            Box(
                                Modifier
                                    .fillMaxWidth()
                                    .padding(topBottom = 7.px, leftRight = 8.px)
                                    .sBorderRadius(8)
                                    .cursor(Cursor.Pointer)
                                    .styleModifier {
                                        if (usersIdx == selectedIndex) {
                                            property("background", "#0A1E42")
                                        } else {
                                            property("background", "transparent")
                                        }
                                        property("transition", "background 100ms ease")
                                    }
                                    .onClick {
                                        onClose()
                                        ctx.router.navigateTo("${Routes.USERS}?q=$q")
                                    }
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    LucideIcon(
                                        name = "users",
                                        size = 14,
                                        color = if (usersIdx == selectedIndex) "#8FB8FF" else "#A7ADB9"
                                    )
                                    Box(Modifier.width(9.px))
                                    SpanText(
                                        "Users: ",
                                        Modifier.styleModifier {
                                            property("font-size", "13px")
                                            property("font-weight", "450")
                                            property("color", if (usersIdx == selectedIndex) "#8FB8FF" else "#A7ADB9")
                                        }
                                    )
                                    SpanText(
                                        "\"$q\"",
                                        Modifier.styleModifier {
                                            property("font-size", "13px")
                                            property("font-weight", "600")
                                            property("color", if (usersIdx == selectedIndex) "#8FB8FF" else "#F0F4FC")
                                        }
                                    )
                                    Box(Modifier.weight(1f))
                                    SpanText(
                                        "Search users →",
                                        Modifier.styleModifier {
                                            property("font-size", "11px")
                                            property("color", "#868A91")
                                        }
                                    )
                                }
                            }
                        }

                        // ── "Clubs: <query>" action row ───────────────────────
                        if (showClubsAction) {
                            val clubsIdx = filtered.size + 1
                            val q = query.trim()
                            Box(
                                Modifier
                                    .fillMaxWidth()
                                    .padding(topBottom = 7.px, leftRight = 8.px)
                                    .sBorderRadius(8)
                                    .cursor(Cursor.Pointer)
                                    .styleModifier {
                                        if (clubsIdx == selectedIndex) {
                                            property("background", "#0A1E42")
                                        } else {
                                            property("background", "transparent")
                                        }
                                        property("transition", "background 100ms ease")
                                    }
                                    .onClick {
                                        onClose()
                                        ctx.router.navigateTo("${Routes.CLUBS}?q=$q")
                                    }
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    LucideIcon(
                                        name = "building-2",
                                        size = 14,
                                        color = if (clubsIdx == selectedIndex) "#8FB8FF" else "#A7ADB9"
                                    )
                                    Box(Modifier.width(9.px))
                                    SpanText(
                                        "Clubs: ",
                                        Modifier.styleModifier {
                                            property("font-size", "13px")
                                            property("font-weight", "450")
                                            property("color", if (clubsIdx == selectedIndex) "#8FB8FF" else "#A7ADB9")
                                        }
                                    )
                                    SpanText(
                                        "\"$q\"",
                                        Modifier.styleModifier {
                                            property("font-size", "13px")
                                            property("font-weight", "600")
                                            property("color", if (clubsIdx == selectedIndex) "#8FB8FF" else "#F0F4FC")
                                        }
                                    )
                                    Box(Modifier.weight(1f))
                                    SpanText(
                                        "Search clubs →",
                                        Modifier.styleModifier {
                                            property("font-size", "11px")
                                            property("color", "#868A91")
                                        }
                                    )
                                }
                            }
                        }
                    }
                }

                // ── Footer hint ───────────────────────────────────────────────
                Row(
                    Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 16.px, topBottom = 10.px)
                        .styleModifier {
                            property("border-top", "1px solid #141C29")
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    KbdHint("↑↓", "navigate")
                    Box(Modifier.width(14.px))
                    KbdHint("↵", "open")
                    Box(Modifier.weight(1f))
                    SpanText(
                        "${totalItems} results",
                        Modifier.styleModifier {
                            property("font-size", "10.5px")
                            property("color", AppColors.FaintKbd.toRgbString())
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun PaletteRow(
    item: NavItem,
    isSelected: Boolean,
    onSelect: () -> Unit
) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 7.px, leftRight = 8.px)
            .sBorderRadius(8)
            .cursor(Cursor.Pointer)
            .styleModifier {
                if (isSelected) {
                    property("background", "#0A1E42")
                } else {
                    property("background", "transparent")
                }
                property("transition", "background 100ms ease")
            }
            .onClick { onSelect() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        LucideIcon(
            name = item.icon,
            size = 14,
            color = if (isSelected) "#8FB8FF" else "#A7ADB9"
        )
        Box(Modifier.width(9.px))
        SpanText(
            item.label,
            Modifier.styleModifier {
                property("font-size", "13px")
                property("font-weight", if (isSelected) "600" else "450")
                property("color", if (isSelected) "#8FB8FF" else "#F0F4FC")
                property("flex", "1")
            }
        )
        Box(Modifier.weight(1f))
        SpanText(
            item.group,
            Modifier.styleModifier {
                property("font-size", "10.5px")
                property("color", AppColors.FaintKbd.toRgbString())
                property("white-space", "nowrap")
            }
        )
    }
}

@Composable
private fun KbdHint(key: String, label: String) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(
            Modifier
                .sBorderRadius(4)
                .padding(leftRight = 5.px, topBottom = 2.px)
                .styleModifier {
                    property("background", "#101828")
                    property("border", "1px solid #1C2634")
                }
        ) {
            SpanText(
                key,
                Modifier.styleModifier {
                    property("font-family", "'JetBrains Mono', monospace")
                    property("font-size", "10px")
                    property("color", "#A7ADB9")
                }
            )
        }
        Box(Modifier.width(4.px))
        SpanText(
            label,
            Modifier.styleModifier {
                property("font-size", "10.5px")
                property("color", "#868A91")
            }
        )
    }
}
